// Read-only projection of runtime paid content. Protected prose stays in Firestore.
export const CANONICAL_ARTICLE_ID = 'item_d424d8ef181955f7a37f977b';
export const ARTICLE_ANNOTATION_ID = 'item_1f5b2baef09a5720f3d15633';
export const CANONICAL_ARTICLE_SHA256 = 'ec333a40677a8c87f4684df72d031521448d979e6f15268b769e6532c4872647';
const OVERVIEW_ID = 'p_article_model_overview_v1';
const PARAGRAPH_IDS = Array.from({ length: 6 }, (_, i) => `p_article_model_p${i + 1}_v1`);
const nonempty = value => typeof value === 'string' && value.trim().length > 0;

export function canonicalArticleSource({ entitled, sourceItem, sourceScope }) {
    if (!entitled || sourceScope !== 'paidItems' || sourceItem?.id !== CANONICAL_ARTICLE_ID
        || sourceItem.type !== 'essay_prompt' || !nonempty(sourceItem.content?.prompt)
        || !nonempty(sourceItem.content?.sampleEssay)) return null;
    return { id: sourceItem.id, prompt: sourceItem.content.prompt, sampleEssay: sourceItem.content.sampleEssay };
}

// The pinned model is one headline followed by six paragraphs. Keep the exact
// paragraph strings; only the blank-line separators become presentation structure.
export function splitCanonicalArticle(sampleEssay) {
    if (!nonempty(sampleEssay)) return null;
    const blocks = sampleEssay.split(/\r?\n[ \t]*\r?\n/).filter(nonempty);
    return blocks.length === 7 ? { headline: blocks[0], paragraphs: blocks.slice(1) } : null;
}

export function canonicalArticleAnnotations(annotationItem, annotationScope) {
    if (annotationScope !== 'paidItems' || annotationItem?.id !== ARTICLE_ANNOTATION_ID
        || annotationItem.type !== 'lit_section') return null;
    const parts = annotationItem.content?.blocks?.parts;
    if (!Array.isArray(parts) || parts.length !== 7) return null;
    const ids = [OVERVIEW_ID, ...PARAGRAPH_IDS];
    if (new Set(parts.map(part => part?.id)).size !== 7) return null;
    const selected = ids.map(id => parts.find(part => part?.id === id));
    if (selected.some(part => !part || !nonempty(part.heading) || !nonempty(part.body))) return null;
    return { overview: selected[0], paragraphs: selected.slice(1) };
}

export async function sha256Text(text, subtle = globalThis.crypto?.subtle) {
    if (typeof text !== 'string' || !subtle?.digest) return null;
    try {
        const digest = await subtle.digest('SHA-256', new TextEncoder().encode(text));
        return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
    } catch { return null; }
}

export async function verifyCanonicalArticle(sampleEssay, subtle = globalThis.crypto?.subtle) {
    const hash = await sha256Text(sampleEssay, subtle);
    return { sampleEssay, status: hash === null ? 'unavailable' : hash === CANONICAL_ARTICLE_SHA256 ? 'matched' : 'changed' };
}

// Verification is bound to the exact current string so a late digest cannot
// attach old annotations to a replacement model. All guidance is withheld until
// that comparison succeeds; a changed model remains available as current content.
export function canonicalArticleStudyView(input) {
    if (!input.entitled) return { state: 'locked' };
    const source = canonicalArticleSource(input);
    if (!source) return { state: 'missing' };
    const verified = input.verification?.sampleEssay === source.sampleEssay ? input.verification.status : 'checking';
    const state = ['matched', 'changed', 'unavailable'].includes(verified) ? verified : 'checking';
    const split = state === 'matched' ? splitCanonicalArticle(source.sampleEssay) : null;
    const notes = split ? canonicalArticleAnnotations(input.annotationItem, input.annotationScope) : null;
    const opening = input.mode === 'opening';
    return {
        state, source, mode: opening ? 'opening' : 'full',
        headline: split?.headline || '',
        paragraphs: split ? (opening ? split.paragraphs.slice(0, 1) : split.paragraphs).map((text, i) => ({ text, annotation: notes?.paragraphs[i] || null })) : [],
        overview: notes && !opening ? notes.overview : null,
        annotationsAvailable: !!notes,
        openingB: opening && split && nonempty(input.openingB) ? input.openingB : '',
    };
}
